import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useAlert } from "react-alert";
import { SettingsContext, UserContext, useEntitlement } from "../../../contexts";
import { Loading } from "../../../components";
import { Visible } from "../../../core/components";
import { isRcOrDaoStaff } from "../../../core/config/storeId";
import { Tabs } from "../../../components/ui";
import {
  AddProductPriceForm,
  ProductPrices,
} from "../../../product/components";
import { useQuoteProductPricing } from "../../hooks/quoteProductPricing";
import StatusBar from "../StatusBar";
import QuoteActions from "../QuoteActions";
import AiPricing from "../AiPricing";

import QuoteForm from "./QuoteForm";
import { MarketDataForm } from "./MarketDataForm";
import { PricingForm } from "./PricingForm";
import Notes from "./Notes";
import PriceIQ from "./PriceIQ";
import "./PriceIQ.scss";
import Updates from "./Updates";
import RequestInfo from "./RequestInfo";
import { CostsForm } from "./CostsForm";
import LeadsList from "./Leads/LeadsList";
import {
  activeTabFn,
  buildTabs,
  findTab,
  getProfitPrices,
} from "./quoteViewHelpers";
import "./rc-quote.scss";

const QuoteView = ({
  id,
  quote,
  isLoading,
  item,
  onQuoteSaved,
  navigateOnSave,
  basePath,
}) => {
  const { isMerchant, iframeQueryParams } = useContext(SettingsContext);
  const { user, activeStoreId } = useContext(UserContext);
  // Data validation screen (Feature 34, Lead Gen) — the AI-Extraction tab.
  const hasDataValidation = useEntitlement("data_validation_screen");
  // Legacy quote pricing workflow (Pricing tab + PRODUCT PRICING / median
  // inputs) — per store; see useQuoteProductPricing.
  const hasProductPricing = useQuoteProductPricing();
  const { pathname } = useLocation();
  // PriceIQ is DAO Watches' proprietary market intelligence, not a tenant
  // feature (yet). Show the cards ONLY for master admins or the CURRENT
  // store (active store if switched, Phase 5 of the multi-store access
  // rework) being the DAO/RC-master store — see isRcOrDaoStaff's own
  // docblock. Hide the whole aside for any other store so tenants never see
  // the "Restricted to RC/DAO staff" placeholder. A per-store add-on flag
  // will later re-enable this for merchants with their own data.
  // …and the Intelligence add-on: without it the cards render nothing, which
  // left their "PriceIQ — …" titles standing over empty boxes.
  const hasPriceIQ = useEntitlement("priceiq_quotes");
  const showPriceIQ = isRcOrDaoStaff(user, activeStoreId) && hasPriceIQ;
  const alert = useAlert();
  const [profitPrices, setProfitPrices] = useState({});
  const [consignPrices, setConsignPrices] = useState({});
  const quoteSubmitRef = useRef(null);
  const [checkUnderPrice, setCheckUnderPrice] = useState({});
  const [shownNoMarketNoticeFor, setShownNoMarketNoticeFor] = useState(null);

  useEffect(() => {
    if (quote) {
      setProfitPrices((prices) =>
        getProfitPrices(quote, prices, hasProductPricing)
      );
    }
  }, [quote, hasProductPricing]);

  const tabs = useMemo(
    () =>
      buildTabs(
        basePath,
        isMerchant,
        item?.request_type,
        hasDataValidation,
        hasProductPricing
      ),
    [basePath, isMerchant, item?.request_type, hasDataValidation, hasProductPricing]
  );
  const isTabActive = (label) =>
    activeTabFn(pathname, findTab(tabs, label), tabs);
  const tabItems = tabs.map((t) => ({
    label: t.label,
    to: `${t.link}?${iframeQueryParams}`,
    exact: true,
  }));
  const isPricingTab = useMemo(
    () => activeTabFn(pathname, findTab(tabs, "Pricing"), tabs),
    [pathname, tabs]
  );
  useEffect(() => {
    if (!isPricingTab) {
      setShownNoMarketNoticeFor(null);
    }
  }, [isPricingTab]);
  useEffect(() => {
    const productId = item?.product?.id;
    if (!isPricingTab || !productId) return;

    if (shownNoMarketNoticeFor === productId) return;

    (async () => {
      try {
        const { data } = await axios.get(
          `/api/products/${productId}/prices/analytics`
        );
        const points12m = Number(
          data?.market?.points_12m ?? data?.market?.year?.points ?? 0
        );

        if (points12m === 0) {
          alert.show(
            "No market prices in the last 12 months. Approve prices from the AI-EXTRACTION tab"
          );
          setShownNoMarketNoticeFor(productId);
        }
      } catch (_e) {
        // The 12-month market-price notice is advisory: it tells the rep where to
        // approve prices. A failed probe must not interrupt opening the tab, and
        // there is nothing for them to do about it.
      }
    })();
  }, [isPricingTab, item?.product?.id, shownNoMarketNoticeFor, alert]);

  if (!quote && isLoading) {
    return <Loading />;
  }

  return (
    <div className="rc-quote">
      <div className="rc-quote__header">
        <div className="rc-quote__section">
          <RequestInfo
            item={item}
            profitPrices={profitPrices}
            quote={quote}
          />
        </div>
        {quote && (
          <div className="row horizontal-bar">
            <StatusBar quote={quote}>
              <QuoteActions quote={quote} onQuoteSaved={onQuoteSaved} />
            </StatusBar>
          </div>
        )}
      </div>

      {/*
        Tab bar — the style guide's Tabs in routing mode. Which tab is active
        stays activeTabFn's call (a link to a tab this quote doesn't have lands
        on Details), so it is forced through activePath; `exact` stops Details'
        NavLink from also matching every sub-tab path under it.
      */}
      <Tabs
        variant="underline"
        aria-label="Quote sections"
        items={tabItems}
        activePath={tabItems.find((t) => isTabActive(t.label))?.to}
      />

      <div className="tabs-content-container">
        <Visible when={isTabActive("Details")}>
          <div className="rc-quote__details-grid">
            <div className="rc-quote__details-main">
              <div className="row rc-quote__row">
                <div className="col-12 col-sm-6">
                  <div className="rc-quote__section">
                    <p className="rc-quote__section-title">Quote</p>
                    <QuoteForm
                      {...{
                        id,
                        quoteSubmitRef,
                        quote,
                        item,
                        onDone: onQuoteSaved,
                        navigateOnSave,
                        setProfitPrices,
                        consignPrices,
                      }}
                    />
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="rc-quote__section rc-quote__section--notes">
                    <p className="rc-quote__section-title">Notes</p>
                    <Notes quote={quote} />
                  </div>
                </div>
              </div>

              <Visible when={quote?.id}>
                <div className="row rc-quote__row">
                  <div className="col-12">
                    <div className="rc-quote__section">
                      <p className="rc-quote__section-title">Costs</p>
                      <CostsForm
                        {...{
                          id,
                          quote,
                          item,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Visible>

              <Visible when={quote?.type === "sell" && quote?.id}>
                <div className="row rc-quote__row">
                  <div className="col-12">
                    <div className="rc-quote__section">
                      <p className="rc-quote__section-title">Market Data CH24</p>
                      <MarketDataForm
                        {...{
                          id,
                          quote,
                          item,
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="row rc-quote__row">
                  <div className="col-12">
                    <div className="rc-quote__section">
                      <p className="rc-quote__section-title">Pricing</p>
                      <PricingForm
                        {...{
                          id,
                          quoteSubmitRef,
                          quote,
                          item,
                          setConsignPrices,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Visible>
            </div>

            {showPriceIQ && (
              <div className="rc-quote__details-aside">
                {/*
                  Two stacked PriceIQ cards:
                  - Top: no-year variant (all-time data, no [YYYY] suffix on price)
                  - Bottom: year-filtered variant (only when item.year is set;
                    would otherwise duplicate the top card)
                */}
                <div className="rc-quote__section rc-quote__section--priceiq">
                  <p className="rc-quote__section-title">PriceIQ — All Years</p>
                  <PriceIQ item={item} isVisible={true} excludeYear={true} />
                </div>
                {item?.year && (
                  <div className="rc-quote__section rc-quote__section--priceiq">
                    <p className="rc-quote__section-title">
                      PriceIQ — {item.year}
                    </p>
                    <PriceIQ item={item} isVisible={true} />
                  </div>
                )}
              </div>
            )}
          </div>
        </Visible>

        <Visible when={isTabActive("Pricing")}>
          <div className="rc-quote__section">
            <p className="rc-quote__section-title">Add Price</p>
            <AddProductPriceForm
              productId={item.product.id}
              item={item.product}
              fieldClassName="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2"
              setCheckUnderPrice={setCheckUnderPrice}
            />
            <ProductPrices
              product={item.product}
              checkUnderPrice={checkUnderPrice}
              basePath={`${basePath}/pricing`}
              variant="embedded"
            />
          </div>
        </Visible>

        <Visible when={isTabActive("AI-Extraction")}>
          <AiPricing
            product={item.product}
            requestItem={item}
            quoteType={item.request_type}
          />
        </Visible>

        <Visible
          when={
            isTabActive("Leads") &&
            item?.request_type === "sell"
          }
        >
          <div className="rc-quote__section">
            <p className="rc-quote__section-title">Leads</p>
            <LeadsList product_id={item.product_id} />
          </div>
        </Visible>

        <Visible
          when={isTabActive("Activity")}
        >
          <div className="rc-quote__section">
            <Updates quote={quote} />
          </div>
        </Visible>
      </div>
    </div>
  );
};

export default QuoteView;
